import { Plus, Trash2, Star } from "lucide-react";
import axios from "axios";

export async function downloadBlob(url, user, name) {
  const r = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` }, responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
}

export const TAGE = ["mo", "di", "mi", "do", "fr", "sa", "so"];
export const TAG_LABELS = { mo: "Montag", di: "Dienstag", mi: "Mittwoch", do: "Donnerstag", fr: "Freitag", sa: "Samstag", so: "Sonntag" };
export const leereTage = () => Object.fromEntries(TAGE.map((t) => [t, { status: "offen", fenster: [] }]));

const STATUS_OPTS = [
  { v: "offen", l: "Noch nicht angegeben", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { v: "nicht_verfuegbar", l: "Nicht verfügbar", cls: "bg-red-50 text-red-700 border-red-200" },
  { v: "verfuegbar", l: "Zeitfenster", cls: "bg-green-50 text-green-700 border-green-200" },
];

function TimeInput({ value, onChange, testId }) {
  return <input type="time" step="300" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-12 w-full min-w-0 rounded-xl border border-slate-200 px-3 text-base text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-600" data-testid={testId} />;
}

function Fehler({ text }) {
  return text ? <p className="text-xs font-semibold text-red-600 mt-1" role="alert">{text}</p> : null;
}

export function TagEditor({ tag, wert, onChange, fehler = {}, prefix, mitBevorzugt, testPrefix }) {
  const t = wert || { status: "offen", fenster: [] };
  const upd = (fn) => onChange((prev) => fn(prev || { status: "offen", fenster: [] }));
  const setStatus = (status) => upd((p) => ({ status, fenster: status === "verfuegbar" ? (p.fenster.length ? p.fenster : [{ von: "", bis: "", bevorzugt: [] }]) : [] }));
  const setFenster = (i, patch) => upd((p) => ({ ...p, fenster: p.fenster.map((f, j) => (j === i ? { ...f, ...(typeof patch === "function" ? patch(f) : patch) } : f)) }));
  const addFenster = () => upd((p) => ({ ...p, fenster: [...p.fenster, { von: "", bis: "", bevorzugt: [] }] }));
  const rmFenster = (i) => upd((p) => { const rest = p.fenster.filter((_, j) => j !== i); return rest.length ? { ...p, fenster: rest } : { status: "offen", fenster: [] }; });
  const key = `${prefix}.${tag}`;
  return (
    <div className={`rounded-2xl border p-4 ${t.status === "verfuegbar" ? "border-green-200 bg-green-50/30" : t.status === "nicht_verfuegbar" ? "border-red-100 bg-red-50/30" : "border-slate-200 bg-white"}`} data-testid={`${testPrefix}-${tag}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-['Manrope'] font-bold text-slate-900">{TAG_LABELS[tag]}</p>
        <div className="flex gap-1.5 flex-wrap">
          {STATUS_OPTS.map((o) => (
            <button key={o.v} type="button" onClick={() => setStatus(o.v)} className={`h-10 px-3 rounded-full text-xs font-semibold border transition-colors ${t.status === o.v ? o.cls + " ring-2 ring-offset-1 ring-cyan-600" : "bg-white text-slate-500 border-slate-200"}`} data-testid={`${testPrefix}-${tag}-${o.v}`}>{o.l}</button>
          ))}
        </div>
      </div>
      <Fehler text={fehler[key]} />
      {t.status === "verfuegbar" && (
        <div className="mt-3 space-y-3">
          {t.fenster.map((f, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 space-y-2" data-testid={`${testPrefix}-${tag}-fenster-${i}`}>
              <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-slate-500">Zeitfenster {i + 1}</span><button type="button" onClick={() => rmFenster(i)} className="h-9 w-9 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center" aria-label="Zeitfenster entfernen" data-testid={`${testPrefix}-${tag}-fenster-${i}-rm`}><Trash2 className="h-4 w-4" /></button></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs font-semibold text-slate-600 block mb-1">Von</label><TimeInput value={f.von} onChange={(v) => setFenster(i, { von: v })} testId={`${testPrefix}-${tag}-${i}-von`} /></div>
                <div><label className="text-xs font-semibold text-slate-600 block mb-1">Bis</label><TimeInput value={f.bis} onChange={(v) => setFenster(i, { bis: v })} testId={`${testPrefix}-${tag}-${i}-bis`} /></div>
              </div>
              <Fehler text={fehler[`${key}.${i}`]} />
              {mitBevorzugt && (
                <div className="pt-1">
                  {(f.bevorzugt || []).map((b, j) => (
                    <div key={j} className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2">
                      <div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1"><Star className="h-3 w-3" />Bevorzugt {j + 1}</span><button type="button" onClick={() => setFenster(i, (ff) => ({ bevorzugt: ff.bevorzugt.filter((_, k) => k !== j) }))} className="text-xs text-amber-800 underline">entfernen</button></div>
                      <div className="grid grid-cols-2 gap-2">
                        <TimeInput value={b.von} onChange={(v) => setFenster(i, (ff) => ({ bevorzugt: ff.bevorzugt.map((x, k) => (k === j ? { ...x, von: v } : x)) }))} testId={`${testPrefix}-${tag}-${i}-bev-${j}-von`} />
                        <TimeInput value={b.bis} onChange={(v) => setFenster(i, (ff) => ({ bevorzugt: ff.bevorzugt.map((x, k) => (k === j ? { ...x, bis: v } : x)) }))} testId={`${testPrefix}-${tag}-${i}-bev-${j}-bis`} />
                      </div>
                      <Fehler text={fehler[`${key}.${i}.bevorzugt.${j}`]} />
                    </div>
                  ))}
                  <button type="button" onClick={() => setFenster(i, (ff) => ({ bevorzugt: [...(ff.bevorzugt || []), { von: "", bis: "" }] }))} className="mt-2 text-xs font-semibold text-amber-800 inline-flex items-center gap-1 h-9" data-testid={`${testPrefix}-${tag}-${i}-bev-add`}><Star className="h-3.5 w-3.5" />Bevorzugte Zeit hinzufügen</button>
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={addFenster} className="w-full h-11 rounded-xl border-2 border-dashed border-cyan-300 text-cyan-800 text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-cyan-50" data-testid={`${testPrefix}-${tag}-add`}><Plus className="h-4 w-4" />Weiteres Zeitfenster</button>
        </div>
      )}
    </div>
  );
}

export function WochenEditor({ tage, onChange, fehler = {}, prefix = "tage", mitBevorzugt = false, testPrefix = "tag" }) {
  const t = tage || leereTage();
  return (
    <div className="space-y-3">
      <Fehler text={fehler[prefix]} />
      {TAGE.map((tag) => <TagEditor key={tag} tag={tag} wert={t[tag]} onChange={(fn) => onChange((prev) => { const pt = prev || leereTage(); return { ...pt, [tag]: fn(pt[tag]) }; })} fehler={fehler} prefix={prefix} mitBevorzugt={mitBevorzugt} testPrefix={testPrefix} />)}
    </div>
  );
}

export function Feld({ label, hint, fehler, children, optional }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 block mb-1.5">{label}{optional && <span className="text-slate-400 font-normal"> (optional)</span>}</label>
      {children}
      {hint && !fehler && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      <Fehler text={fehler} />
    </div>
  );
}

export const inputCls = (err) => `h-12 w-full min-w-0 rounded-xl border px-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-cyan-600 ${err ? "border-red-400" : "border-slate-200"}`;

export function formatApiError(detail, fallback = "Es ist ein Fehler aufgetreten") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (detail.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return fallback;
}

export const STATUS_LABEL = {
  nicht_erfasst: { l: "Noch nicht erfasst", cls: "bg-slate-100 text-slate-600" },
  entwurf: { l: "Entwurf", cls: "bg-amber-50 text-amber-800 border border-amber-200" },
  vollstaendig: { l: "Erfassung vollständig", cls: "bg-green-50 text-green-700 border border-green-200" },
  archiviert: { l: "Archiviert", cls: "bg-slate-200 text-slate-600" },
};
export const RHYTHMUS_LABEL = { woechentlich: "wöchentlich", zweiwoechentlich: "alle zwei Wochen", individuell: "individuell" };
export const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");
